// @flow
/* eslint-disable no-shadow */
import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {fetchChannels} from 'alerts.channels/store/actions';
import {Route, useHistory} from 'react-router-dom';
import {fetchAlertConfigurations} from 'alerts.management/store/actions';
import {fetchUsers} from 'admin.users/store/actions';
import PageLayout from 'common/componentsV2/PageLayout';
import ChannelsHeader from 'channels/components/header/ChannelsHeader';
import ChannelsFilters from 'channels/components/filter/ChannelsFilters';
import ChannelsListHeader from 'channels/components/dataManager/ChannelsListHeader';
import ChannelsListBody from 'channels/components/dataManager/ChannelsListBody';
import {useDispatch, useSelector} from 'react-redux';
import {StringParam, useQueryParams} from 'use-query-params';
import {getActiveChannels} from 'alerts.channels/store/selectors';
import {getUsersData} from 'admin.users/store/selectors';
import DeleteChannel from './DeleteChannel';
import NewEditChannel from './NewEditChannel';
import * as amSelectors from '../../alerts.management/store/selectors';

const getUsedAlerts = (alerts, instance) => alerts.filter((alert) => alert.data.channels.includes(instance.id));

const Channels = ({match, location}: {match: Object, location: Object}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [queryParams] = useQueryParams({
    searchQuery: StringParam,
    type: StringParam,
    owner: StringParam,
  });

  const [selectedItem, setSelectedItem] = useState();
  const [scrollToId, setScrollToId] = useState();

  const onModalClose = useCallback(
    (id) => {
      history.replace(`${match.url}${location.search}`);
      if (id) {
        setSelectedItem(id);
        setScrollToId(id);
      }
    },
    [match, location, setSelectedItem],
  );

  useEffect(() => {
    if (scrollToId) {
      const node = document.getElementById(`scrollPosition-${scrollToId}`);
      if (node) {
        node.scrollIntoView();
        setScrollToId('');
      }
    }
  }, [scrollToId]);

  useEffect(() => {
    dispatch(fetchAlertConfigurations());
    dispatch(fetchChannels());
    dispatch(fetchUsers());
  }, []);

  const alertConfigurations = useSelector(amSelectors.getAlertConfigurations);

  const [areFiltersOpen, setFiltersOpen] = useState(true);
  const [listState, setListState] = useState('EXPAND');

  const setMixedListState = useCallback(() => {
    setListState('MIXED');
  }, [setListState]);

  const collapseAll = useCallback(() => {
    setListState('COLLAPSE');
  }, [setListState]);

  const expandAll = useCallback(() => {
    setListState('EXPAND');
  }, [setListState]);

  const channelsDataRaw = useSelector(getActiveChannels);
  const users = useSelector(getUsersData);

  const usersNamesMap = useMemo(
    () => users.reduce((acc, user) => ({...acc, [user.email]: `${user.firstName} ${user.lastName}`}), {}),
    [users],
  );
  const usersEmailsByID = useMemo(() => users.reduce((acc, user) => ({...acc, [user._id]: user.email}), {}), [users]);
  const channelsData = channelsDataRaw
    .filter((channel) => {
      if (queryParams.searchQuery && !channel.name.includes(queryParams.searchQuery)) {
        return false;
      }
      if (queryParams.type && !queryParams.type.split(',').includes(channel.channelMeta.id)) {
        return false;
      }
      if (
        queryParams.owner &&
        !queryParams.owner
          .split(',')
          .map((id) => usersEmailsByID[id])
          .includes(JSON.parse(channel.tags).owner)
      ) {
        return false;
      }
      return true;
    })
    .map((channel) => ({
      ...channel,
      alerts: getUsedAlerts(alertConfigurations.items, channel),
      createdBy: usersNamesMap[JSON.parse(channel.tags).owner],
    }));

  return (
    <Fragment>
      <div className="position_relative flexGrow_1">
        <PageLayout
          header={
            <ChannelsHeader
              listState={listState}
              dataStreamsCount={channelsDataRaw.length}
              filteredDataStreamsCount={channelsData.length}
              areFiltersOpen={areFiltersOpen}
              setFiltersOpen={setFiltersOpen}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          }
        >
          <div className="display_flex flexGrow_1">
            <ChannelsFilters areFiltersOpen={areFiltersOpen} setFiltersOpen={setFiltersOpen} />
            <div className="flexGrow_1 display_flex flexDirection_column">
              <ChannelsListHeader />
              <ChannelsListBody
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                channelsData={channelsData}
                listState={listState}
                isNoChannels={channelsDataRaw.length === 0}
                setMixedListState={setMixedListState}
              />
            </div>
          </div>
        </PageLayout>
      </div>
      <Route
        path={[`${match.url}/new/:channelType`, `${match.url}/edit/:channelId`]}
        render={(props) => <NewEditChannel {...props} onClose={onModalClose} />}
      />
      <Route
        path={`${match.url}/delete/:channelId`}
        render={(props) => <DeleteChannel {...props} channelsData={channelsData} onClose={onModalClose} />}
      />
    </Fragment>
  );
};

export default Channels;
